import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ShopifyProduct, createShopifyCart, addLineToShopifyCart, updateShopifyCartLine, removeLineFromShopifyCart, fetchShopifyCart } from '@/lib/shopify';

export const GIFT_WRAP_VARIANT_ID = 'gid://shopify/ProductVariant/50387420283213';
export const GIFT_WRAP_PRICE = 3.50;

export interface CartItem {
  lineId: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  quantity: number;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  giftWrap?: boolean;
  giftWrapLineId?: string | null;
}

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;

  addItem: (item: Omit<CartItem, 'lineId' | 'giftWrap' | 'giftWrapLineId'>) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  toggleGiftWrap: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  getCheckoutUrl: () => string | null;
  refreshCheckoutUrl: () => Promise<string | null>;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}


export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isSyncing: false,

      addItem: async (item) => {
        const { items, cartId, clearCart } = get();
        const existingItem = items.find(i => i.variantId === item.variantId);

        set({ isLoading: true });
        try {
          if (!cartId) {
            const result = await createShopifyCart(item.variantId, item.quantity);
            if (result) {
              set({
                cartId: result.cartId,
                checkoutUrl: result.checkoutUrl,
                items: [{ ...item, lineId: result.lineId }],
              });
            }
          } else if (existingItem) {
            const newQuantity = existingItem.quantity + item.quantity;
            if (!existingItem.lineId) {
              console.error('Cannot update item without lineId');
              return;
            }
            const result = await updateShopifyCartLine(cartId, existingItem.lineId, newQuantity);
            if (result.success) {
              const currentItems = get().items;
              set({ items: currentItems.map(i => i.variantId === item.variantId ? { ...i, quantity: newQuantity } : i) });
            } else if (result.cartNotFound) {
              clearCart();
            }
          } else {
            const result = await addLineToShopifyCart(cartId, item.variantId, item.quantity);
            if (result.success) {
              const currentItems = get().items;
              set({ items: [...currentItems, { ...item, lineId: result.lineId ?? null }] });
            } else if (result.cartNotFound) {
              clearCart();
            }
          }
        } catch (error) {
          console.error('Failed to add item:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) {
          await get().removeItem(variantId);
          return;
        }

        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        set({ isLoading: true });
        try {
          const result = await updateShopifyCartLine(cartId, item.lineId, quantity);
          if (result.success) {
            // Also update gift wrap line quantity if present
            if (item.giftWrap && item.giftWrapLineId) {
              await updateShopifyCartLine(cartId, item.giftWrapLineId, quantity);
            }
            const currentItems = get().items;
            set({ items: currentItems.map(i => i.variantId === variantId ? { ...i, quantity } : i) });
          } else if (result.cartNotFound) {
            clearCart();
          }
        } catch (error) {
          console.error('Failed to update quantity:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      removeItem: async (variantId) => {
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item?.lineId || !cartId) return;

        set({ isLoading: true });
        try {
          // Remove gift wrap line first if present
          if (item.giftWrap && item.giftWrapLineId) {
            await removeLineFromShopifyCart(cartId, item.giftWrapLineId);
          }
          const result = await removeLineFromShopifyCart(cartId, item.lineId);
          if (result.success) {
            const currentItems = get().items;
            const newItems = currentItems.filter(i => i.variantId !== variantId);
            newItems.length === 0 ? clearCart() : set({ items: newItems });
          } else if (result.cartNotFound) {
            clearCart();
          }
        } catch (error) {
          console.error('Failed to remove item:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null }),

      toggleGiftWrap: async (variantId) => {
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item || !cartId) return;

        set({ isLoading: true });
        try {
          if (item.giftWrap && item.giftWrapLineId) {
            // Remove gift wrap line
            const result = await removeLineFromShopifyCart(cartId, item.giftWrapLineId);
            if (result.success) {
              const currentItems = get().items;
              set({ items: currentItems.map(i => i.variantId === variantId ? { ...i, giftWrap: false, giftWrapLineId: null } : i) });
            } else if (result.cartNotFound) {
              clearCart();
            }
          } else {
            // Add gift wrap line (quantity matches the item quantity)
            const result = await addLineToShopifyCart(cartId, GIFT_WRAP_VARIANT_ID, item.quantity);
            if (result.success) {
              const currentItems = get().items;
              set({ items: currentItems.map(i => i.variantId === variantId ? { ...i, giftWrap: true, giftWrapLineId: result.lineId ?? null } : i) });
            } else if (result.cartNotFound) {
              clearCart();
            }
          }
        } catch (error) {
          console.error('Failed to toggle gift wrap:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      getCheckoutUrl: () => {
        const rawUrl = get().checkoutUrl?.trim();
        if (!rawUrl) return null;

        const PERMANENT_SHOPIFY_DOMAIN = '50ce58-b1.myshopify.com';

        try {
          const isAbsolute = /^https?:\/\//i.test(rawUrl);
          const parsed = isAbsolute
            ? new URL(rawUrl)
            : new URL(rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`, `https://${PERMANENT_SHOPIFY_DOMAIN}`);

          parsed.protocol = 'https:';
          parsed.hostname = PERMANENT_SHOPIFY_DOMAIN;
          parsed.port = '';
          parsed.hash = '';

          const localeNormalizedPath = parsed.pathname.replace(/^\/[a-z]{2}(?:-[A-Z]{2})?(?=\/cart\/c\/)/, '');
          const cartCheckoutMatch = localeNormalizedPath.match(/^\/cart\/c\/([^/]+)/);
          parsed.pathname = cartCheckoutMatch ? `/checkouts/cn/${cartCheckoutMatch[1]}` : localeNormalizedPath;
          parsed.searchParams.set('channel', 'online_store');

          return parsed.toString();
        } catch {
          const normalizedPath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
          const localeNormalizedPath = normalizedPath.replace(/^\/[a-z]{2}(?:-[A-Z]{2})?(?=\/cart\/c\/)/, '');
          const cartCheckoutMatch = localeNormalizedPath.match(/^\/cart\/c\/([^/]+)/);
          const finalPath = cartCheckoutMatch ? `/checkouts/cn/${cartCheckoutMatch[1]}` : localeNormalizedPath;
          const separator = finalPath.includes('?') ? '&' : '?';
          return `https://${PERMANENT_SHOPIFY_DOMAIN}${finalPath}${separator}channel=online_store`;
        }
      },

      refreshCheckoutUrl: async () => {
        const { cartId, clearCart } = get();
        if (!cartId) return get().getCheckoutUrl();

        try {
          const { exists, checkoutUrl } = await fetchShopifyCart(cartId);
          if (!exists) {
            clearCart();
            return null;
          }

          if (checkoutUrl) {
            set({ checkoutUrl });
          }

          return get().getCheckoutUrl();
        } catch (error) {
          console.error('Failed to refresh checkout URL:', error);
          return get().getCheckoutUrl();
        }
      },

      getTotalItems: () => get().items.reduce((sum, item) => sum + item.quantity, 0),
      getTotalPrice: () => get().items.reduce((sum, item) => {
        let itemTotal = parseFloat(item.price.amount) * item.quantity;
        if (item.giftWrap) itemTotal += GIFT_WRAP_PRICE * item.quantity;
        return sum + itemTotal;
      }, 0),


      syncCart: async () => {
        const { cartId, isSyncing, clearCart } = get();
        if (!cartId || isSyncing) return;

        set({ isSyncing: true });
        try {
          const { exists, checkoutUrl } = await fetchShopifyCart(cartId);
          if (!exists) {
            clearCart();
            return;
          }

          if (checkoutUrl) {
            set({ checkoutUrl });
          }
        } catch (error) {
          console.error('Failed to sync cart:', error);
        } finally {
          set({ isSyncing: false });
        }
      },
    }),
    {
      name: 'shopify-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items, cartId: state.cartId, checkoutUrl: state.checkoutUrl }),
    }
  )
);
